import { UseMutationResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { ProfilesMap } from "@/app/schedules/[tn]/CombinedStore";
import {
    cloneProfile,
    createProfile,
    createProfileWithVoiceSettings,
    deleteProfile,
    getProfile,
    getProfiles,
    getSchedule,
    setActiveProfile,
    setScheduleStatus,
    updateProfile,
    updateSchedule,
} from "@/app/schedules/[tn]/helper";
import { PredefinedSettingsProfile, PredefinedSettingsSchedule, ProfileData } from "@/clients/ntas/profiles";
import { LoggerObject } from "@/utils/logger";
import { CustomError, retryLogic } from "@/utils/network";
export type CheckedState = boolean | "indeterminate";

/**
 * This function is used to parse the profile data and return a map of profileId to profileName.
 * @param profilesData - The profiles array to be parsed.
 * @returns A map of profileId to profileName.
 */
export function parseProfile(profilesData: ProfileData[]): ProfilesMap {
    const loggerObj = new LoggerObject("parseProfile", undefined);
    loggerObj.getLogger().debug("Entered Function");
    return profilesData.reduce((accumulator: ProfilesMap, curr: ProfileData) => {
        if (curr.profileName != null) {
            accumulator[curr.profileId!] = curr.profileName;
        }
        return accumulator;
    }, {});
}

/**
 * This is a react-query hook that creates a new profile for the given phone number.
 * @param tn - The phone number to create the profile for.
 * @param clear - A function to clear the temp settings on a successful creation.
 */
export function useCreateProfile(
    tn: string,
    clear: () => void,
): UseMutationResult<void, CustomError, { profileName: string; parentLoggerObj: LoggerObject }, unknown> {
    const loggerObj = new LoggerObject("useCreateProfile", undefined);
    loggerObj.getLogger().debug("Entered Function");

    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["profiles", tn],
        mutationFn: async ({ profileName, parentLoggerObj }) => {
            const loggerObj = parentLoggerObj.createChildLogger("useCreateProfileMutation");
            loggerObj.getLogger().debug("Entered Function");

            await createProfile(tn, profileName, loggerObj);
        },
        onSuccess: async () => {
            clear();
            await queryClient.invalidateQueries({ queryKey: ["profiles", tn] });
            await queryClient.invalidateQueries({
                queryKey: ["voice-settings", tn],
            });
        },
        onError: (err) => {
            loggerObj.getLogger().debug(err.message);
            toast.error("Something went wrong. Please try again later. \n" + err.message, { duration: 10000 });
        },
        retry: retryLogic,
    });
}

export interface CloneParams {
    profileId: string;
    profileName: string;
}

/**
 * This is a react-query hook that clones a profile for the given phone number.
 * @param tn - The phone number to clone the profile for.
 * @param clear - A function to clear the temp settings on a successful clone.
 */
export function useCloneProfile(
    tn: string,
    clear: () => void,
): UseMutationResult<void, CustomError, { params: CloneParams; parentLoggerObj: LoggerObject }, unknown> {
    const loggerObj = new LoggerObject("useCloneProfile", undefined);
    loggerObj.getLogger().debug("Entered Function");

    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["profiles", tn],
        mutationFn: async ({ params, parentLoggerObj }) => {
            const loggerObj = parentLoggerObj.createChildLogger("useCloneProfileMutation");
            loggerObj.getLogger().debug("Entered Function");

            await cloneProfile(tn, params, loggerObj);
        },
        onSuccess: async () => {
            clear();
            await queryClient.invalidateQueries({ queryKey: ["profiles", tn] });
            await queryClient.invalidateQueries({
                queryKey: ["voice-settings", tn],
            });
        },
        onError: (err) => {
            loggerObj.getLogger().debug(err.message);
            toast.error("Something went wrong. Please try again later. \n" + err.message, { duration: 10000 });
        },
        retry: retryLogic,
    });
}

/**
 * This is a react-query hook that gets the profile for the given phone number and profile ID.
 * @param tn - The phone number to get the profile for.
 * @param profileId - The ID of the profile to get.
 */
export function useProfileQuery(tn: string, profileId: string) {
    const loggerObj = new LoggerObject("useProfileQuery", undefined);
    loggerObj.getLogger().debug("Entered Function");

    return useQuery({
        queryKey: ["profiles", tn, profileId],
        queryFn: () => getProfile(tn, profileId, loggerObj),
        retry: retryLogic,
        enabled: !!tn && tn.length === 11 && !!profileId, // This is to prevent the API call from being made when the TN is invalid.
        staleTime: Infinity, // This is to tell react-query to never refetch the data.
    });
}

/**
 * This is a react-query hook that gets the list of profiles for the given phone number.
 * @param tn - The phone number to get the profiles for.
 */
export function useProfilesQuery(tn: string) {
    const loggerObj = new LoggerObject("useProfilesQuery", undefined);

    return useQuery({
        queryKey: ["profiles", tn],
        queryFn: () => getProfiles(tn, loggerObj),
        retry: retryLogic,
        enabled: !!tn && tn.length === 11, // This is to prevent the API call from being made when the TN is invalid.
        staleTime: Infinity, // This is to tell react-query to never refetch the data.
    });
}

/**
 * This is a react-query hook that sets the active profile for the given phone number.
 * @param tn - The phone number to set the active profile for.
 */
export function useSetActiveProfile(tn: string) {
    const loggerObj = new LoggerObject("useSetActiveProfile", undefined);
    loggerObj.getLogger().debug("Entered Function");

    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["profiles", tn],
        mutationFn: async (profileId: string) => {
            await setActiveProfile(tn, profileId, loggerObj);
        },
        onSuccess: async () => {
            toast.dismiss();
            toast.success("Successfully Activated");
            await queryClient.invalidateQueries({ queryKey: ["profiles", tn] });
            await queryClient.invalidateQueries({
                queryKey: ["voice-settings", tn],
            });
        },
        onError: (err) => {
            loggerObj.getLogger().debug(err);
            toast.error("Something went wrong. Please try again later. \n" + err.message, { duration: 10000 });
        },
        retry: retryLogic,
    });
}

/**
 * This is a react-query hook that updates the profile for the given phone number.
 * @param tn - The phone number whose schedule is to be updated.
 * @param profileId - The ID of the profile to be updated.
 * @param clear - A function to clear the temp settings on a successful update.
 */
export function useUpdateProfile(
    tn: string,
    profileId: string,
    clear?: () => void,
): UseMutationResult<
    void,
    CustomError,
    {
        newProfile: { predefinedSettingsProfile: PredefinedSettingsProfile };
        parentLoggerObj: LoggerObject;
    },
    unknown
> {
    const loggerObj = new LoggerObject("useUpdateProfile", undefined);
    loggerObj.getLogger().debug("Entered Function");

    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["profiles", tn, profileId],
        mutationFn: async ({ newProfile, parentLoggerObj }) => {
            const loggerObj = parentLoggerObj.createChildLogger("useUpdateProfileMutation");
            loggerObj.getLogger().debug("Entered Function");

            await updateProfile(tn, profileId, newProfile, loggerObj);
        },
        onSuccess: async () => {
            clear && clear();
            await queryClient.invalidateQueries({
                queryKey: ["voice-settings", tn],
            });
            await queryClient.invalidateQueries({
                queryKey: ["profiles", tn, profileId],
            });
            await queryClient.invalidateQueries({ queryKey: ["profiles", tn] });
        },
        onError: (err) => {
            loggerObj.getLogger().debug(err);
            toast.error("Something went wrong. Please try again later. \n" + err.message, { duration: 10000 });
        },
        retry: retryLogic,
    });
}

/**
 * This is a react-query hook that deletes a profile for the given phone number.
 * @param tn - The phone number to delete the profile for.
 * @param successMessage - Message to show on deletion success.
 */
export function useDeleteProfile(tn: string, successMessage?: string) {
    const loggerObj = new LoggerObject("useDeleteProfile", undefined);
    loggerObj.getLogger().debug("Entered Function");

    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["profiles", tn],
        mutationFn: async (profileId: string) => {
            await deleteProfile(tn, profileId, loggerObj);
        },
        onSuccess: async () => {
            toast.dismiss();
            successMessage && toast.success(successMessage);
            await queryClient.invalidateQueries({ queryKey: ["profiles", tn] });
            await queryClient.invalidateQueries({
                queryKey: ["voice-settings", tn],
            });
        },
        onError: (err) => {
            loggerObj.getLogger().debug(err);
            toast.error("Something went wrong. Please try again later. \n" + err.message, { duration: 10000 });
        },
        retry: retryLogic,
    });
}

/**
 * This is a react-query hook that creates a new profile for the given phone number.
 * @param tn - The phone number to create the profile for.
 * @param clear - A function to clear the temp settings on a successful creation.
 */
export function useCreateProfileWithVoiceSettings(
    tn: string,
    clear?: () => void,
): UseMutationResult<
    void,
    CustomError,
    {
        newProfile: { predefinedSettingsProfile: PredefinedSettingsProfile };
        parentLoggerObj: LoggerObject;
    },
    unknown
> {
    const loggerObj = new LoggerObject("useCreateProfileWithVoiceSettings", undefined);
    loggerObj.getLogger().debug("Entered Function");

    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["profiles", tn],
        mutationFn: async ({ newProfile, parentLoggerObj }) => {
            const loggerObj = parentLoggerObj.createChildLogger("useCreateProfileMutation");
            loggerObj.getLogger().debug("Entered Function");

            await createProfileWithVoiceSettings(tn, newProfile, loggerObj);
        },
        onSuccess: async () => {
            clear && clear();
            await queryClient.invalidateQueries({ queryKey: ["profiles", tn] });
            await queryClient.invalidateQueries({
                queryKey: ["voice-settings", tn],
            });
        },
        onError: (err) => {
            loggerObj.getLogger().debug(err.message);
            toast.error("Something went wrong. Please try again later. \n" + err.message, { duration: 10000 });
        },
        retry: retryLogic,
    });
}

/**
 * This is a react-query hook that fetches the schedule for the given phone number.
 * @param tn - The phone number whose schedule is to be fetched.
 */
export function useScheduleQuery(tn: string) {
    return useQuery({
        queryKey: ["schedule", tn],
        queryFn: () => getSchedule(tn),
        retry: retryLogic,
        enabled: !!tn && tn.length === 11, // This is to prevent the API call from being made when the TN is invalid.
        staleTime: Infinity, // This is to tell react-query to never refetch the data.
    });
}

/**
 * This is a react-query hook that updates the schedule for the given phone number.
 * @param tn - The phone number whose schedule is to be updated.
 * @param clear - A function to clear the temp settings on a successful update.
 * @param executeOnFailure - A function to execute when the schedules are not updated successfully.
 * @param successMessage - A message to show in toast on success
 */
export const useUpdateSchedule = (tn: string, clear?: () => void, executeOnFailure?: () => void, successMessage?: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["schedule", tn],
        mutationFn: async (newSchedule: { predefinedSettingsSchedule: PredefinedSettingsSchedule }) => {
            await updateSchedule(tn, newSchedule);
        },
        onSuccess: async () => {
            clear && clear();
            toast.dismiss();
            successMessage && toast.success(successMessage);
            await queryClient.invalidateQueries({ queryKey: ["schedule", tn] });
            await queryClient.invalidateQueries({ queryKey: ["profiles", tn] });
            await queryClient.invalidateQueries({
                queryKey: ["voice-settings", tn],
            }); // need to invalidate voice settings
        },
        onError: (err) => {
            executeOnFailure && executeOnFailure();
            toast.error("Something went wrong. Please try again later. \n" + err.message, { duration: 10000 });
        },
        retry: retryLogic,
    });
};

/**
 * This is a react-query hook that sets the schedule status for the given phone number.
 * @param tn - The phone number whose schedule status is to be set.
 * @param callBack - method to be called when the status is set successfully.
 */
export function useSetScheduleStatus(tn: string, callBack: () => void) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["schedule", tn, "status"],
        mutationFn: async (status: boolean) => {
            await setScheduleStatus(tn, status);
        },
        onSuccess: async () => {
            callBack();
            await queryClient.invalidateQueries({ queryKey: ["schedule", tn] });
            await queryClient.invalidateQueries({ queryKey: ["profiles", tn] });
            await queryClient.invalidateQueries({
                queryKey: ["voice-settings", tn],
            }); // need to invalidate voice settings
        },
        onError: (err) => {
            toast.error("Something went wrong. Please try again later. \n" + err.message, { duration: 10000 });
        },
        retry: retryLogic,
    });
}
